import {
  Add,
  ArrowBackIos,
  ArrowForwardIos,
  ArrowRightOutlined,
  CameraAltOutlined,
  DeleteOutlineOutlined,
  Edit,
  GridView,
  ImageOutlined,
  Search,
  Tune,
} from "@mui/icons-material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import oil from "../../assets/images/oil.png";
import battery from "../../assets/svg/batary.svg";
import Carcare from "../../assets/svg/CarCareIcon.svg";
import defoult from "../../assets/svg/defultImage.svg";
import tier from "../../assets/svg/Tire.svg";
import DeleteModal from "../../components/Modals/DeleteModal";

const API_BASE_URL = "https://estbn.com/EstbnNew/public/api";
const IMAGE_BASE_URL = "https://estbn.com/EstbnNew/public/";

const SubCtegoery = () => {
  const [viewMode, setViewMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewBy, setViewBy] = useState("Sub Categories");
  const [subCategories, setSubCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [newUploadedFile, setNewUploadedFile] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const navigate = useNavigate();

  // REMOVED manufacture from defaultValues
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subCategoryName: "",
      category: "",
      description: "",
    },
  });

  const isAbsoluteUrl = (url) =>
    url && (url.startsWith("http://") || url.startsWith("https://"));

  const getAbsoluteImageUrl = useCallback((relativePath) => {
    if (!relativePath) return null;
    if (isAbsoluteUrl(relativePath)) {
      return relativePath;
    }
    const cleanPath = relativePath.startsWith("/")
      ? relativePath.substring(1)
      : relativePath;
    return `${IMAGE_BASE_URL}${cleanPath}`;
  }, []);

  const fetchCategories = useCallback(
    async (currentPage, currentPerPage, currentSearchTerm) => {
      setLoading(true);
      if (!token) {
        toast.error("Authentication token not available.");
        setLoading(false);
        setSubCategories([]);
        setRowCount(0);
        return;
      }
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            type: "sub",
            page: currentPage,
            per_page: currentPerPage,
            search: currentSearchTerm,
          },
        };
        const response = await axios.get(
          `${API_BASE_URL}/portal/equipment-categories`,
          config
        );
        if (response.data.success) {
          const categoriesData = response.data.data?.categories || [];
          const categoriesWithAbsoluteImages = categoriesData.map((cat) => ({
            ...cat,
            full_profile_image_url: cat.full_profile_image_url
              ? getAbsoluteImageUrl(cat.full_profile_image_url)
              : defoult,
          }));
          setSubCategories(categoriesWithAbsoluteImages);
          setRowCount(response.data.data?.pagination?.total || 0);
        } else {
          toast.error("Failed to fetch sub-categories.");
          setSubCategories([]);
          setRowCount(0);
        }
      } catch (error) {
        console.error("Error fetching sub-categories:", error);
        toast.error("Error fetching sub-categories.");
        setSubCategories([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [token, getAbsoluteImageUrl, defoult]
  );

  const fetchMainCategories = useCallback(async () => {
    if (!token) {
      toast.error("Authentication token not available for main categories.");
      setMainCategories([]);
      return;
    }
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          type: "main",
        },
      };
      const response = await axios.get(
        `${API_BASE_URL}/portal/equipment-categories`,
        config
      );
      if (response.data.success) {
        setMainCategories(response.data.data?.categories || []);
      } else {
        toast.error("Failed to fetch main categories.");
        setMainCategories([]);
      }
    } catch (error) {
      console.error("Error fetching main categories:", error);
      toast.error("Error fetching main categories.");
      setMainCategories([]);
    }
  }, [token]);

  // Keeping fetchManufacturers for the Table display, even though the input is removed
  const fetchManufacturers = useCallback(async () => {
    if (!token) {
      toast.error("Authentication token not available for manufacturers.");
      setManufacturers([]);
      return;
    }
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${API_BASE_URL}/portal/manufacturers`,
        config
      );
      if (response.data.success) {
        setManufacturers(response.data.data?.manufacturers || []);
      } else {
        toast.error("Failed to fetch manufacturers.");
        setManufacturers([]);
      }
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
      toast.error("Error fetching manufacturers.");
      setManufacturers([]);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchMainCategories();
      fetchManufacturers();
    }
  }, [token, fetchMainCategories, fetchManufacturers]);

  useEffect(() => {
    if (token) {
      const handler = setTimeout(() => {
        fetchCategories(
          paginationModel.page + 1,
          paginationModel.pageSize,
          searchTerm
        );
      }, 300);
      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    token,
    paginationModel.page,
    paginationModel.pageSize,
    searchTerm,
    fetchCategories,
  ]);

  const handleAddShow = () => {
    setItemToEdit(null);
    setNewUploadedFile(null);
    // REMOVED manufacture from reset
    reset({
      subCategoryName: "",
      category: "",
      description: "",
    });
    setShowAddEditModal(true);
  };

  const handleEditShow = (item) => {
    setItemToEdit(item);
    setNewUploadedFile(null);

    // REMOVED manufacture from reset
    reset({
      subCategoryName: item.name || "",
      category: item.parent_id || "",
      description: item.description || "",
    });
    setShowAddEditModal(true);
  };

  const handleAddEditClose = () => {
    setShowAddEditModal(false);
    setItemToEdit(null);
    setNewUploadedFile(null);
    reset();
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteShow = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !token) {
      toast.error("Missing data or token for deletion.");
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.delete(
        `${API_BASE_URL}/portal/equipment-categories/${itemToDelete.id}`,
        config
      );

      if (response.data.success) {
        toast.success("Sub Category deleted successfully!");
        handleDeleteClose();
        fetchCategories(
          paginationModel.page + 1,
          paginationModel.pageSize,
          searchTerm
        );
      } else {
        toast.error(response.data.message || "Failed to delete sub category.");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error(
        error.response?.data?.message || "Error deleting sub category."
      );
    }
  };

  const handleDeleteProfileImage = async (e) => {
    e.stopPropagation();
    if (!itemToEdit || !token) {
      toast.error("Cannot delete image: no item selected or token missing.");
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/portal/equipment-categories/${itemToEdit.id}/profile-image`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Profile image deleted successfully!");
      setItemToEdit((prev) => ({
        ...prev,
        full_profile_image_url: defoult,
      }));
      setNewUploadedFile(null);
      fetchCategories(
        paginationModel.page + 1,
        paginationModel.pageSize,
        searchTerm
      );
    } catch (error) {
      console.error(
        "Error deleting profile image:",
        error.response ? error.response.data : error.message
      );
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to delete image: ${errorMessage}`);
    }
  };

  const onSubmit = async (data) => {
    if (!token) {
      toast.error("Authentication token is missing. Please log in.");
      return;
    }
    try {
      const categoryFormData = new FormData();
      categoryFormData.append("name", data.subCategoryName);
      if (data.category) {
        categoryFormData.append("parent_id", data.category);
      }
      categoryFormData.append("description", data.description);
      categoryFormData.append(
        "background_color",
        itemToEdit?.background_color || "#F8F8F8"
      );
      categoryFormData.append("order", 9);
      categoryFormData.append("is_active", 1);
      categoryFormData.append("slug", data.subCategoryName);

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      let response;
      let categoryId = itemToEdit?.id;

      if (itemToEdit) {
        categoryFormData.append("_method", "PUT");
        response = await axios.post(
          `${API_BASE_URL}/portal/equipment-categories/${itemToEdit.id}`,
          categoryFormData,
          config
        );
        categoryId = itemToEdit.id;
      } else {
        response = await axios.post(
          `${API_BASE_URL}/portal/equipment-categories`,
          categoryFormData,
          config
        );
        categoryId = response.data.data.category?.id;
      }

      if (response.data.success) {
        toast.success(
          `Sub Category ${itemToEdit ? "updated" : "added"} successfully!`
        );

        if (newUploadedFile && categoryId) {
          const imageUploadFormData = new FormData();
          imageUploadFormData.append("profile_image", newUploadedFile);

          try {
            await axios.post(
              `${API_BASE_URL}/portal/equipment-categories/${categoryId}/profile-image`,
              imageUploadFormData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            toast.success("Sub Category image uploaded successfully!");
          } catch (imageUploadError) {
            console.error(
              "Error uploading sub category image:",
              imageUploadError.response
                ? imageUploadError.response.data
                : imageUploadError.message
            );
            toast.error("Failed to upload sub category image.");
          }
        }

        handleAddEditClose();
        fetchCategories(
          paginationModel.page + 1,
          paginationModel.pageSize,
          searchTerm
        );
      } else {
        toast.error(
          response.data.message ||
            `Failed to ${itemToEdit ? "update" : "add"} sub category.`
        );
      }
    } catch (error) {
      console.error("Submit Error:", error);
      const errors = error.response?.data?.errors;
      let errorMessage = error.response?.data?.message || error.message;

      if (errors) {
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey) {
          errorMessage = errors[firstErrorKey][0];
        }
      }
      toast.error(`Failed to save sub category: ${errorMessage}.`);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewUploadedFile(file);
    } else {
      setNewUploadedFile(null);
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "name",
      headerName: "Sub category Name",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "itemsNumber",
      headerName: "Items Number",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Actions",
      headerName: "Action",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-evenly align-items-center h-100 w-100">
            <IconButton
              sx={{ color: "" }}
              aria-label="edit"
              size="small"
              style={{ width: "25%" }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditShow(params.row);
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteShow(params.row);
              }}
              aria-label="delete"
              size="small"
              color="error"
              style={{ width: "25%" }}
            >
              <DeleteOutlineOutlined />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/EQmodal`);
              }}
              aria-label="navigate"
              size="small"
              color="default"
              style={{ width: "25%" }}
            >
              <ArrowRightOutlined />
            </IconButton>
          </div>
        );
      },
    },
  ];

  const mappedTableData = Array.isArray(subCategories)
    ? subCategories.map((item) => {
        const manufacturerId =
          item.equipment_models && item.equipment_models.length > 0
            ? item.equipment_models[0].manufacturer_id
            : null;
        const manufacturerName = manufacturerId
          ? manufacturers.find((m) => m.id == manufacturerId)?.name || ""
          : "";

        return {
          id: item.id,
          name: item.name,
          category: item.parent?.name || "",
          parent_id: item.parent_id,
          itemsNumber: item.equipment_models_count,
          manufacturer: manufacturerName,
          manufacturer_id: manufacturerId,
          full_profile_image_url: item.full_profile_image_url,
          description: item.description,
          background_color: item.background_color,
        };
      })
    : [];

  const getCardImage = (card) => {
    if (
      card.full_profile_image_url &&
      card.full_profile_image_url !== defoult
    ) {
      return card.full_profile_image_url;
    }
    switch (card.parent?.name) {
      case "Tires":
        return tier;
      case "Oils and Filters":
        return oil;
      case "Car Care":
        return Carcare;
      case "Batteries":
        return battery;
      default:
        return defoult;
    }
  };

  const getCardBackgroundColor = (card) => {
    return card.background_color || "#F8F8F8";
  };

  const imageToDisplayInModal = newUploadedFile
    ? URL.createObjectURL(newUploadedFile)
    : itemToEdit
    ? itemToEdit.full_profile_image_url
    : null;
  const finalImageForModal = imageToDisplayInModal || defoult;

  const PaginationControls = ({
    paginationModel,
    setPaginationModel,
    rowCount,
    loading,
  }) => {
    const currentPage = paginationModel.page + 1;
    const pageSize = paginationModel.pageSize;
    const totalPages = Math.ceil(rowCount / pageSize);
    const from = rowCount === 0 ? 0 : paginationModel.page * pageSize + 1;
    const to = Math.min((paginationModel.page + 1) * pageSize, rowCount);

    const handlePageSizeChange = (event) => {
      setPaginationModel((prev) => ({
        ...prev,
        pageSize: parseInt(event.target.value, 10),
        page: 0,
      }));
    };

    const handlePreviousPage = () => {
      if (paginationModel.page > 0) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }));
      }
    };

    const handleNextPage = () => {
      if (paginationModel.page < totalPages - 1) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }));
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 2,
          gap: 1,
          flexWrap: "wrap",
          padding: "8px 16px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Rows per page:
        </Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            displayEmpty
            inputProps={{ "aria-label": "Rows per page" }}
            sx={{ maxHeight: 36 }}
          >
            {[5, 10, 25, 50, 100].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          variant="body2"
          color="textSecondary"
        >{`${from}-${to} of ${rowCount}`}</Typography>
        <IconButton
          onClick={handlePreviousPage}
          disabled={paginationModel.page === 0 || loading}
          size="small"
        >
          <ArrowBackIos fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={paginationModel.page >= totalPages - 1 || loading}
          size="small"
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const handleViewByChange = (event) => {
    const selectedValue = event.target.value;
    setViewBy(selectedValue);

    if (selectedValue === "Main Categories") {
      navigate("/equipments");
    } else if (selectedValue === "Models") {
      if (subCategories.length > 0) {
        navigate(`/EQmodal`);
      } else {
        toast.info("No subcategories available to view models for.");
      }
    } else if (selectedValue === "All Equipment") {
      navigate(`/AllEquipments`);
    }
  };

  return (
    <>
      <Modal
        show={showAddEditModal}
        onHide={handleAddEditClose}
        dialogClassName="custom-modal-size"
      >
        <Modal.Header style={{ backgroundColor: "#D5D5D5" }}>
          <Modal.Title className="text-black">
            {itemToEdit ? "Edit Sub Category" : "Add Sub Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex flex-column align-items-center mb-4">
              <div
                className="position-relative"
                style={{ width: "100px", height: "100px" }}
              >
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    border: "2px dashed #ccc",
                    overflow: "hidden",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {finalImageForModal && finalImageForModal !== defoult ? (
                    <img
                      src={finalImageForModal}
                      alt="Uploaded"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageOutlined sx={{ fontSize: 40, color: "#ccc" }} />
                  )}
                </div>
                <label
                  htmlFor="image-upload"
                  className="position-absolute bottom-0 end-0 p-1 bg-dark rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                  style={{ transform: "translate(25%, 25%)" }}
                >
                  <CameraAltOutlined sx={{ fontSize: 20, color: "#fff" }} />
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </label>
                {itemToEdit &&
                  itemToEdit.full_profile_image_url &&
                  itemToEdit.full_profile_image_url !== defoult &&
                  !newUploadedFile && (
                    <IconButton
                      onClick={handleDeleteProfileImage}
                      className="position-absolute bottom-0 start-0 p-1 bg-danger rounded-circle d-flex justify-content-center align-items-center"
                      style={{ transform: "translate(-25%, 25%)", zIndex: 1 }}
                      size="small"
                      aria-label="delete image"
                    >
                      <DeleteOutlineOutlined
                        sx={{ fontSize: 20, color: "#fff" }}
                      />
                    </IconButton>
                  )}
              </div>
            </div>

            <div className="my-2">
              <label htmlFor="subCategoryName">
                Sub category Name <span className="text-danger">*</span>
              </label>
              <TextField
                size="small"
                className="w-100"
                type="text"
                placeholder="Enter Name"
                {...register("subCategoryName", {
                  required: "Sub category name is required",
                })}
              />
            </div>

            <div className="my-2">
              <label htmlFor="category">Category</label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    id="category-autocomplete"
                    options={mainCategories}
                    getOptionLabel={(option) => option.name || ""}
                    // Handle the value to match the ID stored in form state
                    value={
                      mainCategories.find((c) => c.id === field.value) || null
                    }
                    // Update form state with ID
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.id : "");
                    }}
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option.name}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select Category"
                        className="w-100"
                      />
                    )}
                  />
                )}
              />
            </div>

            {/* REMOVED MANUFACTURE INPUT FIELD HERE */}

            <div className="my-3">
              <label htmlFor="description">Sub category Description</label>
              <textarea
                style={{
                  padding: 10,
                  borderRadius: 15,
                  border: "solid 2px #D8D8D8",
                }}
                rows={3}
                className="w-100"
                placeholder="Add Description"
                {...register("description")}
              />
            </div>

            <div className="d-flex gap-3 justify-content-center align-items-center custom-card-sm my-3">
              <button
                type="button"
                onClick={handleAddEditClose}
                className="btn btn-light px-5"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-dark px-5">
                {itemToEdit ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <div className="m-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h6 className="m-0">Equipment</h6>
          <button onClick={handleAddShow} className="btn btn-light mx-1">
            <Add /> New Sub Category
          </button>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center w-100 my-3">
          <div
            className="input-group flex-nowrap justify-content-end align-items-center rounded-2"
            style={{ width: "400px", maxWidth: "100%" }}
          >
            <input
              type="text"
              className="form-control bg-transparent position-relative rounded"
              placeholder="Search for equipment .."
              value={searchTerm}
              onChange={handleSearch}
              style={{ paddingRight: "40px" }}
            />
            <div className="position-absolute mx-2">
              <Search color="action" />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 w-50">
            <span>View by</span>
            <Select
              className="w-25"
              size="small"
              value={viewBy}
              onChange={handleViewByChange}
              MenuProps={{
                PaperProps: {
                  style: { padding: "8px" },
                },
              }}
            >
              <MenuItem
                key="Main Categories"
                value="Main Categories"
                className="custom-menu-item"
              >
                Main Categories
              </MenuItem>
              <MenuItem
                key="Sub Categories"
                value="Sub Categories"
                className="custom-menu-item"
              >
                Sub Categories
              </MenuItem>
              <MenuItem
                key="Models"
                value="Models"
                className="custom-menu-item"
              >
                Models
              </MenuItem>
              <MenuItem
                key="All Equipment"
                value="All Equipment"
                className="custom-menu-item"
              >
                All Equipment
              </MenuItem>
            </Select>

            <div className="">
              <button
                onClick={() => setViewMode("cards")}
                className={`btn ${
                  viewMode === "cards" ? "btn-dark" : "btn-light"
                }`}
              >
                <GridView />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`btn ${
                  viewMode === "table" ? "btn-dark" : "btn-light"
                }`}
              >
                <FormatListBulletedIcon />
              </button>
            </div>

            <div className="">
              <button className="btn btn-light">
                <Tune />
              </button>
            </div>
          </div>
        </div>

        {loading && viewMode === "table" ? (
          <div className="text-center my-5">Loading categories...</div>
        ) : (
          <>
            {viewMode === "cards" && (
              <>
                <div className="row">
                  {Array.isArray(subCategories) &&
                    subCategories.map((card) => (
                      <div key={card.id} className="col-12 col-lg-3">
                        <div
                          style={{
                            backgroundColor: getCardBackgroundColor(card),
                          }}
                          className="rounded-3 px-3 py-2 my-2 w-100"
                          onClick={() => navigate(`/EQmodal`)}
                        >
                          <div className="d-flex justify-content-between align-items-center my-1 flex-wrap custom-card-sm gap-2">
                            <div className="d-flex justify-content-center align-items-center custom-card-sm">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditShow(card);
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="cursor">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteShow(card);
                                  }}
                                >
                                  <DeleteOutlineOutlined />
                                </IconButton>
                              </div>
                            </div>
                          </div>

                          <div className="cursor text-center">
                            {getCardImage(card) ? (
                              <img
                                src={getCardImage(card)}
                                alt={card.name}
                                style={{
                                  width: 80,
                                  height: 80,
                                  margin: "10px auto",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 80,
                                  height: 80,
                                  margin: "10px auto",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#e0e0e0",
                                  borderRadius: "50%",
                                }}
                              >
                                <ImageOutlined
                                  sx={{ fontSize: 40, color: "#9e9e9e" }}
                                />
                              </div>
                            )}
                            <h6 className="fw-bold text-center my-2">
                              {card.name}
                            </h6>
                            <p
                              className="text-muted"
                              style={{
                                fontSize: "0.85rem",
                                minHeight: "4.5em",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {card.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                <PaginationControls
                  paginationModel={paginationModel}
                  setPaginationModel={setPaginationModel}
                  rowCount={rowCount}
                  loading={loading}
                />
              </>
            )}

            {viewMode === "table" && (
              <div className="data-grid-small">
                <Paper sx={{ height: "auto", width: "100%" }}>
                  <DataGrid
                    autoHeight
                    rows={mappedTableData}
                    columns={columns}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    loading={loading}
                    sx={{ border: 0 }}
                    checkboxSelection
                    disableRowSelectionOnClick
                  />
                </Paper>
              </div>
            )}
          </>
        )}
      </div>

      <DeleteModal
        showDelete={showDeleteModal}
        handleDeleteClose={handleDeleteClose}
        textMessage="Are you sure you want to delete this equipment sub-category? This action cannot be undone."
        deleteFunction={confirmDelete}
        itemName={itemToDelete?.name || ""}
      />
    </>
  );
};

export default SubCtegoery;
